import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Modal, Button, MuiSelect, useAsync, useForm } from '@hometap/htco-components';
import { GET_REVIEWERS_DATA } from 'apps/track-details/ApplicationReview/data/queries/getReviewersData';
import ActionsTooltip from 'components/Tooltip/ActionsTooltip';
import LoadingContainer from 'components/LoadingContainer/LoadingContainer';
import ActionTooltipOptions from 'data/constants/actionTooltipOptions';
import { assignStaffToTrack } from 'data/trackRequest';
import { Reviewer } from 'hooks/types/types';
import { StaffSelectPopupProps } from '../types/types';
import { showNotification, TOAST_TYPE } from 'utils/toasts';
import { 
  isInAdminGroup, 
  isInApplicationSpecialistGroup, 
  isInSalesManagerGroup, 
  isInUnderwriterGroup, 
  REVIEWER_ROLES 
} from 'userRoles';

interface FormData {
  application_specialist?: string;
  underwriter?: string;
}

const StaffSelectPopup = ({ 
  isAppSpecialist, 
  initialStaff, 
  trackId, 
  user, 
  refetch 
}: StaffSelectPopupProps) => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [staff, setStaff] = useState(initialStaff);

  // Matching permissions with user roles that can access the assign endpoint
  const flagEnabled = user?.rolloutFlags.includes('assign_staff');
  const hasPermissions = isInUnderwriterGroup(user) || 
    isInApplicationSpecialistGroup(user) || 
    isInAdminGroup(user) || 
    isInSalesManagerGroup(user);
  const isEditable = flagEnabled && hasPermissions;

  const { loading: loadingReviewers = true, data } = useQuery(GET_REVIEWERS_DATA);
  const roleTitle = isAppSpecialist ? REVIEWER_ROLES.APPLICATION_SPECIALIST : REVIEWER_ROLES.UNDERWRITER;
  
  // Map the staff list to format for the select component
  const staffList = isAppSpecialist ? data?.appSpecialists?.edges : data?.underwriters?.edges;
  const staffChoices = staffList?.map(({ node }: { node: Reviewer }) => ({
    value: node.user?.identifier,
    label: `${node.user?.firstName} ${node.user?.lastName}`
  }));

  const formDefaults: FormData = isAppSpecialist 
    ? { application_specialist: staff?.identifier } 
    : { underwriter: staff?.identifier };

  const { registerField, handleSubmit } = useForm(formDefaults);

  useEffect(() => {
    setStaff(initialStaff);
  }, [initialStaff]);

  const { execute } = useAsync(assignStaffToTrack, {
    onSuccess: () => {
      showNotification({
        type: TOAST_TYPE.success,
        title: 'Underwriting Field Updated',
        description: `${roleTitle} Updated`,
      });
      refetch?.();
      setShowEditModal(false);
    },
    onError: (error: string) => {
      setStaff(initialStaff);
      showNotification({
        type: TOAST_TYPE.error,
        title: 'Failed to save',
        description: `Could not update the value please try again ${error}`,
      });
      setShowEditModal(false);
    },
  });

  const onSubmit = (formData: FormData) => {
    const [[role, userId]] = Object.entries(formData);
    execute(trackId, role, userId);
  };

  let formattedFieldValue = '-';
  if (staff === undefined) {
    formattedFieldValue = isEditable ? 'Assign user' : '-';
  } else {
    formattedFieldValue = `${staff?.firstName || ''} ${staff?.lastName || ''}`;
  }

  return (
    <LoadingContainer isLoading={loadingReviewers}>
      {isEditable ? (
        <ActionsTooltip
          value={formattedFieldValue}
          actions={[
            {
              actionType: ActionTooltipOptions.Edit,
              onEdit: () => setShowEditModal(true),
            },
          ]}
        >
          {formattedFieldValue}
        </ActionsTooltip>
      ) : (
        formattedFieldValue
      )}

      {showEditModal && (
        <Modal 
          className="UnderwritingModal" 
          open={showEditModal} 
          onClose={() => setShowEditModal(false)} 
          width={536}
        >
          <form 
            className="UnderwritingModalDetails" 
            onSubmit={handleSubmit(onSubmit)}
          >
            <h1 className="UnderwritingModalTitle">Edit</h1>
            <label>{`Select a new user for ${roleTitle}`}</label>
            <MuiSelect 
              className="UnderwritingModalSelect" 
              theme="outlined"  
              options={staffChoices} 
              value={staff?.identifier} 
              width='100%' 
              {...registerField(isAppSpecialist ? 'application_specialist' : 'underwriter')}
            />
            <Button 
              type="submit" 
              className="UnderwritingModalButton" 
              size="large"
            >
              Save
            </Button>
          </form>
        </Modal>
      )}
    </LoadingContainer>
  );
};

export default StaffSelectPopup;
